import { computed, onScopeDispose, ref } from "vue"
import type { ComputedRef, Ref } from "vue"
import { useQuery, useSubscription } from "@vue/apollo-composable"
import { serializeError } from "serialize-error"
import {
  CommunityActivityIndicatorDocument,
  CommunityActivityIndicatorChangedDocument,
} from "@/graphql/types"
import type { ActivityIndicatorData } from "@/AppContext"
import { onWsReconnect } from "@/graphql/wsConnection"
import { createLogger } from "@/logger"

const logger = createLogger("ActivityIndicator")

export type IndicatorBadgeColor = "error" | "success"
export interface IndicatorBadge {
  color: IndicatorBadgeColor
  count: number
}

export function badgeFor(state: Ref<ActivityIndicatorData>): ComputedRef<IndicatorBadge | null> {
  return computed(() => {
    if (state.value.pendingModerationCount > 0) {
      return { color: "error", count: state.value.pendingModerationCount }
    }
    if (state.value.unreadCount > 0) {
      return { color: "success", count: state.value.unreadCount }
    }
    return null
  })
}

export function useActivityIndicator() {
  const state = ref<ActivityIndicatorData>({ unreadCount: 0, pendingModerationCount: 0 })
  const lastReadAt = ref<string | null>(null)

  const { onResult, onError, refetch } = useQuery(
    CommunityActivityIndicatorDocument,
    {},
    { fetchPolicy: "no-cache" },
  )
  onResult((r) => {
    const data = r.data
    if (data?.activityIndicator) {
      state.value = {
        unreadCount: data.activityIndicator.unreadCount,
        pendingModerationCount: data.activityIndicator.pendingModerationCount,
      }
    }
    if (data?.activityReadState) {
      lastReadAt.value = String(data.activityReadState.lastReadAt)
    }
  })
  onError((error) => {
    logger.error({ error: serializeError(error) }, "activity indicator query failed")
  })

  const { onResult: onSubscription, onError: onSubscriptionError } = useSubscription(
    CommunityActivityIndicatorChangedDocument,
  )
  onSubscription((r) => {
    const changed = r.data?.activityIndicatorChanged
    if (changed) {
      state.value = {
        unreadCount: changed.unreadCount,
        pendingModerationCount: changed.pendingModerationCount,
      }
    }
  })
  onSubscriptionError((error) => {
    logger.error({ error: serializeError(error) }, "activity indicator subscription failed")
  })

  // After a WS drop/reconnect, events missed during the gap are never re-delivered,
  // so the badge count can go stale. Refetch to reconcile (resilience-05), mirroring
  // useCommunityStream. onScopeDispose ties the listener to the owning effect scope.
  const disposeReconnect = onWsReconnect(() => refetch())
  onScopeDispose(disposeReconnect)

  return { state, lastReadAt, refetch }
}
